import { useDriverManagement } from '@/api/useDriverManagement'
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Drawer, List, Modal, Result, Skeleton, Tooltip, notification } from 'antd'
import { cloneDeep } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ShiftTileList } from './ShiftTiles/ShiftTileList'
import { TemplateForm, type TemplateInput } from './TemplateForm'
import { TemplateHeader } from './TemplateHeader'
import { TemplatePreview } from './TemplatePreview'
import { daysOfTheWeek } from './utils'
import './templateManagement.css'

interface TemplateDrawerProps {
  isVisible: boolean
  onClose: () => void
}

function TemplateDrawerContent() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null | undefined>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [templateForm, setTemplateForm] = useState<TemplateInput>()
  const [notificationApi, contextHolder] = notification.useNotification()

  const { useDriverTemplates, useDeleteTemplate, useUpsertTemplate } = useDriverManagement()
  const { data: templates, isLoading: templatesLoading, isError: templatesError } = useDriverTemplates()
  const { mutateAsync: deleteTemplateMutation, isPending: isDeletingTemplate } = useDeleteTemplate()
  const { mutateAsync: upsertTemplate } = useUpsertTemplate()

  const selectedTemplate = useMemo(
    () => templates?.find((x) => x.id === selectedTemplateId),
    [templates, selectedTemplateId]
  )

  const confirmDeleteTemplate = useCallback(() => {
    if (!confirmDelete || !selectedTemplateId) return

    return deleteTemplateMutation({ body: { id: selectedTemplateId } })
      .then(() => {
        notificationApi.success({ message: 'Template deleted successfully' })
        setSelectedTemplateId(undefined)
      })
      .catch(() => notificationApi.error({ message: 'Failed to delete template' }))
      .finally(() => setConfirmDelete(false))
  }, [confirmDelete, deleteTemplateMutation, selectedTemplateId, notificationApi])

  const handleSubmit = useCallback(
    (body: TemplateInput) =>
      upsertTemplate({ body })
        .then((res) => {
          notificationApi.success({ message: 'Template created successfully' })
          setSelectedTemplateId(res?.id)
          setTemplateForm(undefined)
        })
        .catch(() => {
          notificationApi.error({ message: 'Failed to create template' })
        }),
    [notificationApi, upsertTemplate]
  )

  const handleAddTemplate = useCallback(() => {
    setTemplateForm({ name: '', days: [] })
  }, [])

  // Auto-select first template when drawer opens
  useEffect(() => {
    if (!selectedTemplateId && typeof templates?.[0]?.id === 'string') {
      setSelectedTemplateId(templates[0].id)
    }
  }, [templates, selectedTemplateId])

  const statusMessage = useMemo(() => {
    if (templatesLoading) return <Skeleton active />

    if (templatesError)
      return (
        <Result
          status='error'
          title='Error loading templates'
          subTitle='An error has occured while retrieving schedule templates'
        />
      )

    if (!templates?.length) return <Texto category='h2'>Create a template to get starteed</Texto>

    return null
  }, [templatesError, templates, templatesLoading])

  const templateEditor = useMemo(() => {
    if (templateForm)
      return (
        <TemplateForm
          initial={templateForm}
          onClose={() => setTemplateForm(undefined)}
          onSubmit={handleSubmit}
          header={templateForm?.id ? `Editing ${templateForm.name ?? 'Existing Template'}` : undefined}
        />
      )

    if (selectedTemplate)
      return (
        <div className='template-container mt-4'>
          <TemplateHeader
            title={selectedTemplate.name}
            actions={
              <>
                <GraviButton buttonText='Edit' onClick={() => setTemplateForm(cloneDeep(selectedTemplate))} />
                <GraviButton
                  buttonText='Duplicate'
                  icon={<CopyOutlined />}
                  onClick={() =>
                    setTemplateForm({
                      ...cloneDeep(selectedTemplate),
                      id: undefined,
                      name: `${selectedTemplate.name} (Copy)`,
                    })
                  }
                />
                <GraviButton
                  buttonText='Delete'
                  error
                  icon={<DeleteOutlined />}
                  onClick={() => setConfirmDelete(true)}
                />
              </>
            }
          >
            <Texto appearance='hint'>{selectedTemplate.description || 'No description'}</Texto>

            <Horizontal gap={16}>
              <Horizontal gap={4} alignItems='center'>
                <Texto category='label'>Days:</Texto>
                <Texto weight='bold'>{selectedTemplate.days?.length}</Texto>
              </Horizontal>
              {/* Display Starts On if applicable and it's set */}
              {selectedTemplate.offset_day !== null && selectedTemplate.offset_day !== undefined && (
                <Horizontal gap={4} alignItems='center'>
                  <Texto category='label'>Starts:</Texto>
                  <Texto weight='bold'>{daysOfTheWeek[selectedTemplate.offset_day]}</Texto>
                </Horizontal>
              )}
            </Horizontal>
          </TemplateHeader>
          <Vertical>
            <ShiftTileList days={selectedTemplate.days} offsetDay={selectedTemplate.offset_day ?? undefined} />
          </Vertical>
        </div>
      )

    return (
      <Horizontal fullHeight verticalCenter horizontalCenter>
        {statusMessage}
      </Horizontal>
    )
  }, [templateForm, selectedTemplate, statusMessage, handleSubmit])

  return (
    <>
      {contextHolder}
      {/* Confirmation Modal for delete */}
      <Modal
        title='Confirm Delete'
        open={confirmDelete && !!selectedTemplateId}
        onOk={confirmDeleteTemplate}
        confirmLoading={isDeletingTemplate}
        onCancel={() => setConfirmDelete(false)}
        okText='Delete'
        cancelText='Cancel'
      >
        <p>Are you sure you want to delete this template? This action cannot be undone.</p>
      </Modal>

      <Horizontal bg='default' height='100%'>
        {/* Template List */}
        <div style={{ width: 300, borderRight: '1px solid var(--gray-300)' }}>
          <Vertical>
            <Horizontal bg='elevated' border='bottom' className='p-3' alignItems='center' style={{ flexShrink: 0 }}>
              <Texto style={{ flex: 1 }} category='h4'>
                Templates
              </Texto>
              <Tooltip
                placement='bottomRight'
                title={
                  templateForm ? 'Save or discard your current changes to create a new template. →' : 'Create Template'
                }
              >
                <GraviButton
                  size='small'
                  icon={<PlusOutlined />}
                  theme2
                  onClick={handleAddTemplate}
                  disabled={!!templateForm}
                />
              </Tooltip>
            </Horizontal>
            <List
              dataSource={templates || []}
              style={{ overflowY: 'auto' }}
              renderItem={(template) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedTemplateId === template.id ? 'var(--theme-color-2-dim)' : 'transparent',
                    borderRadius: 4,
                    padding: '8px 16px',
                    marginBottom: '4px',
                  }}
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <Vertical gap={4} style={{ width: '100%' }}>
                    <Texto category='p2'>{template.name}</Texto>
                    <Horizontal alignItems='center' gap={8}>
                      <Texto category='label'>{template.days?.length} day</Texto>
                      <TemplatePreview value={template.days?.map((x) => x.shift)} />
                    </Horizontal>
                  </Vertical>
                </List.Item>
              )}
            />
          </Vertical>
        </div>

        <div style={{ flex: 1, padding: '0px 24px' }}>
          {/* Template Editor */}
          {templateEditor}
        </div>
      </Horizontal>
    </>
  )
}

export function TemplateDrawer({ isVisible, onClose }: TemplateDrawerProps) {
  return (
    <Drawer
      title='Schedule Template Management'
      placement='bottom'
      height='95vh'
      styles={{ body: { padding: 0, minWidth: 1400 } }}
      onClose={onClose}
      open={isVisible}
      destroyOnClose
    >
      <TemplateDrawerContent />
    </Drawer>
  )
}
